/**
 * @fileoverview Error Recovery Hook
 *
 * Bu hook, hata durumlarında kullanıcıya alternatif çözümler sunan
 * recovery handler'larını sağlar.
 *
 * @module hooks/useErrorRecovery
 */

import { useCallback } from "react";
import { ERROR_ACTIONS } from "../utils";

/**
 * @typedef {Object} UseErrorRecoveryParams
 * @property {Function} callSearch - Arama fonksiyonu
 * @property {Function} setCallError - Hata state setter
 * @property {Object} query - Mevcut arama sorgusu
 * @property {string} currentSort - Mevcut sıralama
 */

/**
 * @typedef {Object} UseErrorRecoveryReturn
 * @property {Function} handleRetrySearch - Aramayı tekrarla
 * @property {Function} handleRemoveFiltersAndSearch - Filtreleri kaldırarak ara
 * @property {Function} handleExpandLocation - Konumu genişleterek ara
 */

/**
 * Hata kurtarma işlemleri için hook.
 *
 * Bu hook, arama hataları durumunda kullanıcıya sunulan
 * alternatif çözümleri (yeniden dene, filtreleri kaldır,
 * konumu genişlet) sağlar.
 *
 * @function useErrorRecovery
 * @param {UseErrorRecoveryParams} params - Hook parametreleri
 * @returns {UseErrorRecoveryReturn} Error recovery handler'ları
 *
 * @example
 * const {
 *   handleRetrySearch,
 *   handleRemoveFiltersAndSearch,
 *   handleExpandLocation,
 * } = useErrorRecovery({
 *   callSearch,
 *   setCallError,
 *   query,
 *   currentSort,
 * });
 *
 * // ErrorRecovery bileşeninde kullanım
 * <ErrorRecovery
 *   onRetry={handleRetrySearch}
 *   onRemoveFilters={handleRemoveFiltersAndSearch}
 *   onExpandLocation={handleExpandLocation}
 * />
 */
export function useErrorRecovery({ callSearch, setCallError, query, currentSort }) {
  /**
   * Aramayı mevcut parametrelerle tekrarla
   */
  const handleRetrySearch = useCallback(() => {
    if (!query?.path) return;
    setCallError(null);
    callSearch({
      path: query.path,
      filters: query.filters,
      sort: currentSort,
    });
  }, [query?.path, query?.filters, currentSort, callSearch, setCallError]);

  /**
   * Filtreleri kaldırarak ara
   */
  const handleRemoveFiltersAndSearch = useCallback(() => {
    if (!query?.path) return;
    setCallError(null);
    callSearch({
      path: query.path,
      filters: undefined,
      sort: currentSort,
    });
  }, [query?.path, currentSort, callSearch, setCallError]);

  /**
   * Konumu genişleterek ara (mahalle -> ilçe veya ilçe -> il)
   *
   * @param {string} action - "expand-district" veya "expand-city"
   */
  const handleExpandLocation = useCallback((action) => {
    if (!query?.path) return;

    const pathParts = query.path.split("/");
    if (pathParts.length < 2) return;

    const typePart = pathParts[0]; // satilik-konut
    const locationPart = pathParts[1]; // istanbul-kadikoy-fenerbahce
    const locationSegments = locationPart.split("-");

    let newPath;
    if (action === ERROR_ACTIONS.EXPAND_DISTRICT && locationSegments.length > 2) {
      // Mahalle -> İlçe (son segmenti kaldır)
      const newLocation = locationSegments.slice(0, 2).join("-");
      newPath = `${typePart}/${newLocation}`;
    } else if (action === ERROR_ACTIONS.EXPAND_CITY && locationSegments.length > 1) {
      // İlçe -> İl (sadece ili bırak)
      newPath = `${typePart}/${locationSegments[0]}`;
    }

    if (newPath) {
      setCallError(null);
      callSearch({
        path: newPath,
        filters: query.filters,
        sort: currentSort,
      });
    }
  }, [query?.path, query?.filters, currentSort, callSearch, setCallError]);

  return {
    handleRetrySearch,
    handleRemoveFiltersAndSearch,
    handleExpandLocation,
  };
}

export default useErrorRecovery;
